import css from "./DeviceItem.sass?inline"
import {isDefined, Lifecycle, Nullable} from "@opendaw/lib-std"
import {createElement} from "@opendaw/lib-jsx"
import {Html} from "@opendaw/lib-dom"
import {IndexedBox} from "@opendaw/lib-box"
import {InstrumentFactories} from "@opendaw/studio-adapters"
import {EffectFactories, PresetEntry} from "@opendaw/studio-core"
import {IconSymbol} from "@opendaw/studio-enums"
import {DragAndDrop} from "@/ui/DragAndDrop"
import {AnyDragData, DragDevice} from "@/ui/AnyDragData"
import {PresetService} from "@/ui/browse/PresetService"
import {PresetItems} from "@/ui/browse/PresetItem"
import {Icon} from "../components/Icon"

const className = Html.adoptStyleSheet(css, "DeviceItem")

export type StockDeviceMeta = {
    key: string
    name: string
    icon: IconSymbol
    brief: string
    externalIconUrl?: string
    presetless?: boolean
}
export type DeviceDropKind = "audio-effect" | "midi-effect"

type Construct = {
    presetService: PresetService
    expandedKeys: Set<string>
    device: StockDeviceMeta
    presets: ReadonlyArray<PresetEntry>
    expandOnRender: boolean
    onCreate: () => void
    dropKind: Nullable<DeviceDropKind>
    onDrop: Nullable<(effects: ReadonlyArray<IndexedBox>) => Promise<void>>
    instrumentKey: Nullable<InstrumentFactories.Keys>
    expandKey: string
    lifecycle: Lifecycle
}

export const DeviceItem = ({
                               presetService, expandedKeys, device, presets, expandOnRender,
                               onCreate, dropKind, onDrop, instrumentKey, expandKey, lifecycle
                           }: Construct): HTMLElement => {
    const empty = presets.length === 0
    const item: HTMLElement = <div className={Html.buildClassList(className, empty && "empty")}/>
    const triangle: HTMLElement = <span className="triangle"/>
    const header: HTMLElement = (
        <div className="device-header">
            {triangle}
            {isDefined(device.externalIconUrl)
                ? <div className="icon external">
                    <img src={device.externalIconUrl} alt=""/>
                </div>
                : <div className="icon">
                    <Icon symbol={device.icon}/>
                </div>}
            <span className="name">{device.name}</span>
            <span className="brief">{device.brief}</span>
        </div>
    )
    const presetList: HTMLElement = <div className="preset-list hidden"/>
    presetList.append(...PresetItems(presets, presetService, lifecycle))
    const shouldExpand = !empty && (expandedKeys.has(expandKey) || expandOnRender)
    if (shouldExpand) {
        presetList.classList.remove("hidden")
        item.classList.add("expanded")
    }
    triangle.onclick = (event: MouseEvent) => {
        event.stopPropagation()
        if (empty) {return}
        const open = !presetList.classList.toggle("hidden")
        item.classList.toggle("expanded", open)
        if (open) {expandedKeys.add(expandKey)} else {expandedKeys.delete(expandKey)}
    }
    header.onclick = () => onCreate()
    if (dropKind === "audio-effect") {
        DragAndDrop.installSource(header, () => ({
            type: "audio-effect",
            uuids: null,
            device: device.key as EffectFactories.AudioEffectKeys
        } satisfies DragDevice))
    } else if (dropKind === "midi-effect") {
        DragAndDrop.installSource(header, () => ({
            type: "midi-effect",
            uuids: null,
            device: device.key as EffectFactories.MidiEffectKeys
        } satisfies DragDevice))
    } else {
        DragAndDrop.installSource(header, () => ({
            type: "instrument",
            device: device.key as InstrumentFactories.Keys
        } satisfies DragDevice))
    }
    const acceptsEffect = isDefined(dropKind) && isDefined(onDrop)
    const acceptsInstrument = isDefined(instrumentKey)
    const isRackIntentEffect = (dragData: AnyDragData): boolean =>
        (dragData.type === "audio-effect" || dragData.type === "midi-effect")
        && dragData.uuids !== null
        && isDefined(dragData.instrument)
    const isBareInstrument = (dragData: AnyDragData): boolean =>
        dragData.type === "instrument" && dragData.device === null && dragData.effects.length === 0
    if (acceptsEffect || acceptsInstrument) {
        DragAndDrop.installTarget(header, {
            drag: (_event, dragData) => {
                if (acceptsEffect && !isRackIntentEffect(dragData)) {
                    const effects = presetService.resolveEffectBoxesFromDrag(dropKind, dragData)
                    if (effects.length === 1 && EffectFactories.keyOfBox(effects[0]) === device.key) {
                        return true
                    }
                }
                if (acceptsInstrument && isBareInstrument(dragData)) {
                    return presetService.resolveDraggedInstrumentKey(dragData) === instrumentKey
                }
                return false
            },
            drop: (_event, dragData) => {
                if (acceptsEffect && !isRackIntentEffect(dragData)) {
                    const effects = presetService.resolveEffectBoxesFromDrag(dropKind, dragData)
                    if (effects.length === 1 && EffectFactories.keyOfBox(effects[0]) === device.key) {
                        onDrop(effects).catch(console.warn)
                        header.classList.remove("accept-drop")
                        return
                    }
                }
                if (acceptsInstrument && isBareInstrument(dragData)
                    && presetService.resolveDraggedInstrumentKey(dragData) === instrumentKey
                    && dragData.type === "instrument" && dragData.device === null) {
                    presetService.saveAsInstrumentPreset(instrumentKey, dragData.uuid).catch(console.warn)
                }
                header.classList.remove("accept-drop")
            },
            enter: allowDrop => header.classList.toggle("accept-drop", allowDrop),
            leave: () => header.classList.remove("accept-drop")
        })
    }
    item.appendChild(header)
    item.appendChild(presetList)
    return item
}
